"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { UIOptionsType } from "@rjsf/utils";
import useSWR, { mutate } from "swr";

import ScenarioForm from "@/src/components/ScenarioForm";
import Modal from "@/src/components/Modal";
import Loader from "@/src/components/Loader";
import { createUI } from "@/src/utils/createUI";
import { fetcher } from "@/src/utils/fetcher";
import { findQuestionIdsByTitles, findAnswersByQuestionIds } from "@/src/utils/findAnswersByTitles";
import { validateCode } from "@/src/utils/microsoftAuth";
//types
import { formValues } from "@/src/types/api";
import { JsonSchema } from "@/src/types/utils/createUI";

// Type guard to check for error response
function isErrorResponse(data: unknown): data is { error: string } {
  return typeof data === 'object' && data !== null && 'error' in data;
}

export default function CreateScenarioPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [newScenarioId, setNewScenarioId] = useState(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const router = useRouter();

  // Fetch page data
  const { data: generalPageFormData, error: pageError } = useSWR<JsonSchema>(
    `/api/page/Aanvraag`,
    fetcher,
  );

  // Create UI options from the page data
  const uiJson: UIOptionsType | null = generalPageFormData && !isErrorResponse(generalPageFormData)
    ? createUI(generalPageFormData)
    : null;

  // Combine errors
  const error = pageError;
  // Loading state
  const loading = !generalPageFormData && !pageError;

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleActionSuccess = () => {
    router.push(`https://www.hetnieuwenormaal.nl/`);
    setModalOpen(false);
  };

  const handleSubmit = async (formData: formValues) => {
    //reset state in case of multiple submissions
    setModalOpen(false);
    setErrorMessage(null);
    //reset new scenario id in case of multiple submissions
    if (newScenarioId) {
      setNewScenarioId(null);
    }
    //if nothing is filled in, return early
    const entries = Object.entries(formData);
    if (entries.length === 0) {
      return { status: "failure", message: "No data to save" };
    }

    // Extract important metadata fields from the formData
    const titles: string[] = ["titel", "E-mail aanvrager", "Projectnaam", "Sector", "Code"];
    const ids = findQuestionIdsByTitles(generalPageFormData || null, titles);
    const values = findAnswersByQuestionIds(formData, ids);

    // Construct a name from the available fields - use Projectnaam as primary, 
    // fall back to Titel, or use a default if neither is available
    const projectName = (values["Projectnaam"] || values["Titel"] || "") as string;
    const emailAanvrager = values["E-mail aanvrager"] as string || "";
    const sector = values["Sector"] as string || "";
    const code = values["Code"] as string || "";

    // Validate the code if provided
    if (code) {
      try {
        console.log('Validating code:', code);
        const validationResult = await validateCode(code);

        console.log('Code validation result:', validationResult);

        // Check if status is active
        if (validationResult.data.status !== 'active') {
          const errorMsg = `De opgevoerde code is al in gebruik voor een evaluatie. Neem contact op met info@hetnieuwenormaal.nl voor verdere ondersteuning.`;
          setErrorMessage(errorMsg);
          setModalOpen(true);
          return { status: "failure", message: errorMsg };
        }

        console.log('Code validation successful, status is actief');
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Code validatie mislukt';
        setErrorMessage(`Code validatie fout: ${errorMsg}`);
        setModalOpen(true);
        return { status: "failure", message: errorMsg };
      }
    }

    const scenarioMetaData = {
      name: projectName,
      description: "",
      is_active: true,
      aanvrager: emailAanvrager,
      sector: sector,
      code: code,
      answers: formData,
    };

    try {
      const response = await fetch("/api/scenarios", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(scenarioMetaData),
      });
      const data = await response.json();

      if (response.ok && data.id) {
        console.log("Scenario created with ID:", data.id);
        setNewScenarioId(data.id);
        setModalOpen(true);
        // Ververs de SWR cache
        mutate('/api/page/Aanvraag');
        return { status: "success", id: data.id };
      } else {
        // Handle API error response
        const errorMsg = data.error || "Failed to create scenario";
        setErrorMessage(errorMsg);
        setModalOpen(true);
        return { status: "failure", message: errorMsg };
      }
    } catch (error) {
      const errorMsg = (error as Error).message;
      setErrorMessage(errorMsg);
      setModalOpen(true);
      mutate('/api/page/Aanvraag');
      return { status: "failure", message: errorMsg };
    }
  };

  return (
    <div className="min-w-screen">
      <div className="page_container ">
        <h1 className="header_underline">Vraag een evaluatie aan</h1>
        {(error || isErrorResponse(generalPageFormData) || loading || (generalPageFormData?.description && !isErrorResponse(generalPageFormData))) && (
          <div className="white_box">
            <div className="flex flex-col">
              {error && (
                <p className="">Er is een fout opgetreden bij het laden van de pagina.</p>
              )}
              {isErrorResponse(generalPageFormData) && (
                <p className="">Er is een fout opgetreden bij het laden van de pagina. Probeer het later opnieuw.</p>
              )}
              {loading && (
                <div className="flex flex-row justify-center py-6 w-full">
                  <Loader />
                </div>
              )}
              {generalPageFormData?.description && !isErrorResponse(generalPageFormData) && (
                <p className="my-4 text-primary">{generalPageFormData.description}</p>
              )}
            </div>
          </div>
        )}
        <div className="mt-4">
          {generalPageFormData && !isErrorResponse(generalPageFormData) && (
            <ScenarioForm
              formJson={generalPageFormData}
              uiSchema={uiJson}
              formValues={null}
              handleSubmit={handleSubmit}
              buttonText="Vraag evaluatie aan"
            />
          )}
        </div>
        {modalOpen && (
          <Modal
            isOpen={modalOpen}
            onClose={handleCloseModal}
            title={
              newScenarioId ? "Evaluatie aangevraagd" : "Oeps, er ging iets mis"
            }
            message={
              newScenarioId
                ? "De aanvraag is gelukt. U ontvangt een e-mail met daarin een link naar het formulier en de gekoppelde evaluator"
                : errorMessage || "Er is een fout opgetreden bij het aanmaken van de evaluatie. Probeer het opnieuw."
            }
            actionButtonText={
              newScenarioId ? "Terug naar homepage" : "Nogmaals proberen"
            }
            onAction={
              newScenarioId ? handleActionSuccess : handleCloseModal
            }
          />
        )}
      </div>
    </div>
  );
}
